import type { HotkeyManagerConfig } from "./";

export const DEFAULT_CONFIG: Required<HotkeyManagerConfig> = Object.freeze({
  enableByDefault: true,
  requireModifiers: false,
  persistPreferences: true,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  storage: undefined as any, //* is set at runtime
  globalScope: "global",
  autoResolveConflicts: true,
  logWarnings: false,
});

export const STORAGE_KEYS = Object.freeze({
  PREFERENCES: "user-preferences",
  CUSTOM_MAPPINGS: "custom-mappings",
  DISABLED_HOTKEYS: "disabled-hotkeys",
} as const);

export const CSS_CLASSES = Object.freeze({
  FOCUS_VISIBLE: "crx-hotkey-focus-visible",
  SCOPE_ACTIVE: "crx-hotkey-scope-active",
  INDICATOR: "crx-hotkey-indicator",
  SR_ONLY: "sr-only",
} as const);

export const ARIA_ATTRS = Object.freeze({
  HOTKEY_SCOPE: "data-hotkey-scope",
  HOTKEY_HINT: "data-hotkey-hint",
  FOCUS_VISIBLE: "data-focus-visible",
} as const);

export const EVENTS = Object.freeze({
  REGISTERED: "hotkey:registered",
  UNREGISTERED: "hotkey:unregistered",
  TRIGGERED: "hotkey:triggered",
  INVALID: "hotkey:invalid",
  PREFERENCES_TOGGLED: "preferences:toggled",
  REMAPPED: "hotkey:remapped",
  SCOPE_ACTIVATED: "scope:activated",
  SCOPE_DEACTIVATED: "scope:deactivated",
} as const);

const eventNameCache = new Map<string, boolean>();
const storageKeyCache = new Map<string, boolean>();
const cssClassCache = new Map<string, boolean>();
const ariaAttrCache = new Map<string, boolean>();

export function isValidEventName(eventName: string): boolean {
  if (eventNameCache.has(eventName)) {
    return eventNameCache.get(eventName)!;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isValid = Object.values(EVENTS).includes(eventName as any);
  eventNameCache.set(eventName, isValid);
  return isValid;
}

export function isValidStorageKey(key: string): boolean {
  if (storageKeyCache.has(key)) {
    return storageKeyCache.get(key)!;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isValid = Object.values(STORAGE_KEYS).includes(key as any);
  storageKeyCache.set(key, isValid);
  return isValid;
}

export function isValidCssClass(className: string): boolean {
  if (cssClassCache.has(className)) {
    return cssClassCache.get(className)!;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isValid = Object.values(CSS_CLASSES).includes(className as any);
  cssClassCache.set(className, isValid);
  return isValid;
}

export function isValidAriaAttr(attrName: string): boolean {
  if (ariaAttrCache.has(attrName)) {
    return ariaAttrCache.get(attrName)!;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isValid = Object.values(ARIA_ATTRS).includes(attrName as any);
  ariaAttrCache.set(attrName, isValid);
  return isValid;
}

let eventNamesCache: string[] | null = null;
export function getAllEventNames(): readonly string[] {
  if (eventNamesCache === null) {
    eventNamesCache = Object.values(EVENTS);
  }
  return eventNamesCache;
}

let storageKeysCache: string[] | null = null;
export function getAllStorageKeys(): readonly string[] {
  if (storageKeysCache === null) {
    storageKeysCache = Object.values(STORAGE_KEYS);
  }
  return storageKeysCache;
}

let cssClassesCache: string[] | null = null;
export function getAllCssClasses(): readonly string[] {
  if (cssClassesCache === null) {
    cssClassesCache = Object.values(CSS_CLASSES);
  }
  return cssClassesCache;
}

let ariaAttrsCache: string[] | null = null;
export function getAllAriaAttrs(): readonly string[] {
  if (ariaAttrsCache === null) {
    ariaAttrsCache = Object.values(ARIA_ATTRS);
  }
  return ariaAttrsCache;
}

export function clearDefaultsCaches(): void {
  eventNameCache.clear();
  storageKeyCache.clear();
  cssClassCache.clear();
  ariaAttrCache.clear();
  eventNamesCache = null;
  storageKeysCache = null;
  cssClassesCache = null;
  ariaAttrsCache = null;
}

export function getDefaultsCacheStats() {
  return {
    eventNameCache: eventNameCache.size,
    storageKeyCache: storageKeyCache.size,
    cssClassCache: cssClassCache.size,
    ariaAttrCache: ariaAttrCache.size,
    hasEventNamesCache: eventNamesCache !== null,
    hasStorageKeysCache: storageKeysCache !== null,
    hasCssClassesCache: cssClassesCache !== null,
    hasAriaAttrsCache: ariaAttrsCache !== null,
  };
}
